(function (root) {
  'use strict';

  const namespace = root.Kate3DPreview = root.Kate3DPreview || {};
  if (namespace.CatalogService) return;

  const EXCLUDED_PRODUCT_SKUS = new Set(['D104443', 'XP02895']);
  const EXCLUDED_PRODUCT_HANDLES = new Set([
    'kate-pvc-non-slip-mat-for-fixing-backdrop',
    'kate-double-sided-reflective-sheeting-gold-silver-floor-for-photography',
    'rts-kate-double-sided-aisle-runner-mirror-rugs-floor-reflective-gold-silver-floor-for-photography-u-s-only',
  ]);
  const EXCLUDED_PRODUCT_TITLES = new Set([
    'kate transparent pvc reflective clear floor mat for cake smash',
    'rts kate transparent pvc reflective clear floor mat for cake smash (u.s. only)',
  ]);
  const EXCLUDED_SEARCH_PRODUCT_TYPES = new Set(['combo sets', 'combos sets', 'room set']);

  const normalizeValue = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();

  class CatalogService {
    constructor({ itemsPerPage = 20, allowedProductTypes = [] } = {}) {
      this.itemsPerPage = itemsPerPage;
      this.allowedProductTypes = new Set(allowedProductTypes);
      this.api = null;
      this.destroyed = false;
      this.wallPages = new Map();
      this.wallStates = new Map([[1, { cursor: null, overflow: [], hasMoreRaw: true }]]);
      this.floorPages = new Map();
      this.floorStates = new Map([[1, { cursor: null, overflow: [], hasMoreRaw: true }]]);
      this.searchQuery = '';
      this.searchGeneration = 0;
      this.searchPages = new Map();
      this.searchCursors = new Map([[1, null]]);
      this.productGalleries = new Map();
      this.productGalleryRequests = new Map();
    }

    async init() {
      this.api = await this.waitForStorefrontAPI();
      return this;
    }

    waitForStorefrontAPI() {
      return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (api) => {
          if (settled || !api) return;
          settled = true;
          clearTimeout(timeout);
          resolve(api);
        };
        const timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error('ShopifyStorefrontAPI loading timed out.'));
        }, 10000);

        if (root.ShopifyStorefrontAPI) {
          if (typeof root.ShopifyStorefrontAPI.onInit === 'function') {
            root.ShopifyStorefrontAPI.onInit(() => finish(root.ShopifyStorefrontAPI));
          } else {
            finish(root.ShopifyStorefrontAPI);
          }
          return;
        }

        document.addEventListener('ShopifyStorefrontAPI:init', (event) => {
          finish(event.detail?.api || root.ShopifyStorefrontAPI);
        }, { once: true });
      });
    }

    assertReady() {
      if (this.destroyed) throw new Error('3D preview catalog service was destroyed.');
      if (!this.api) throw new Error('ShopifyStorefrontAPI is not ready.');
    }

    async loadDefaultProducts({ wallHandle, floorHandle } = {}) {
      this.assertReady();
      const load = async (handle) => {
        if (!handle) return null;
        try {
          const product = await this.api.getProductImages(handle);
          this.seedProductGallery(product);
          return product;
        } catch (error) {
          if (error.name !== 'AbortError') console.error(`Unable to load default product ${handle}:`, error);
          return null;
        }
      };
      const [wall, floor] = await Promise.all([load(wallHandle), load(floorHandle)]);
      return { wall, floor };
    }

    async loadProductsByVariantIds({ wallVariantId, floorVariantId } = {}) {
      this.assertReady();
      const requests = [
        { type: 'wall', id: wallVariantId },
        { type: 'floor', id: floorVariantId },
      ].filter((request) => request.id);
      const products = { wall: null, floor: null };

      if (!requests.length) return products;

      const results = await this.api.getProductsByVariantIds(requests.map((request) => request.id));
      requests.forEach((request, index) => {
        const product = results[index] || null;
        if (!product) return;
        products[request.type] = product;
        this.seedProductGallery(product);
      });
      return products;
    }

    seedProductGallery(product) {
      if (!product?.handle || !Array.isArray(product.images) || !product.images.length) return;
      this.productGalleries.set(product.handle, product.images);
    }

    async loadProductGallery(product) {
      this.assertReady();
      const handle = product?.handle;
      if (!handle) return [];
      if (this.productGalleries.has(handle)) return this.productGalleries.get(handle);
      if (this.productGalleryRequests.has(handle)) return this.productGalleryRequests.get(handle);

      const request = this.api.getProductImages(handle)
        .then((details) => {
          const images = Array.isArray(details.images) ? details.images : [];
          if (images.length) this.productGalleries.set(handle, images);
          return images;
        })
        .finally(() => {
          this.productGalleryRequests.delete(handle);
        });

      this.productGalleryRequests.set(handle, request);
      return request;
    }

    async loadWallPage(collectionHandle, page = 1) {
      this.assertReady();
      if (this.wallPages.has(page)) return this.wallPages.get(page);

      const state = this.wallStates.get(page);
      if (!state) throw new Error(`Backdrop page ${page} is not available.`);

      const candidates = [...state.overflow];
      let cursor = state.cursor;
      let hasMoreRaw = state.hasMoreRaw;

      while (candidates.length < this.itemsPerPage && hasMoreRaw) {
        const result = await this.api.getCollectionProducts(collectionHandle, {
          limit: Math.max(this.itemsPerPage * 2, 40),
          cursor,
          sortKey: 'MANUAL',
        });
        const filteredProducts = result.products.filter((product) => (
          this.allowedProductTypes.size === 0 || this.allowedProductTypes.has(product.productType)
        ));
        candidates.push(...filteredProducts);
        cursor = result.nextCursor || null;
        hasMoreRaw = Boolean(result.hasNextPage && cursor);
      }

      const products = candidates.slice(0, this.itemsPerPage);
      const overflow = candidates.slice(this.itemsPerPage);
      const hasNextPage = overflow.length > 0 || hasMoreRaw;
      const snapshot = {
        products,
        page,
        hasNextPage,
        hasPreviousPage: page > 1,
      };

      this.wallPages.set(page, snapshot);
      if (hasNextPage) {
        this.wallStates.set(page + 1, { cursor, overflow, hasMoreRaw });
      }
      return snapshot;
    }

    async loadFloorPage(collectionHandle, page = 1) {
      this.assertReady();
      if (this.floorPages.has(page)) return this.floorPages.get(page);
      const state = this.floorStates.get(page);
      if (!state) throw new Error(`Floor page ${page} is not available.`);

      const candidates = [...state.overflow];
      let cursor = state.cursor;
      let hasMoreRaw = state.hasMoreRaw;

      while (candidates.length < this.itemsPerPage && hasMoreRaw) {
        const result = await this.api.getCollectionProducts(collectionHandle, {
          limit: Math.max(this.itemsPerPage * 2, 40),
          cursor,
          sortKey: 'MANUAL',
        });
        candidates.push(...result.products.filter((product) => !this.isExcludedProduct(product)));
        cursor = result.nextCursor || null;
        hasMoreRaw = Boolean(result.hasNextPage && cursor);
      }

      const products = candidates.slice(0, this.itemsPerPage);
      const overflow = candidates.slice(this.itemsPerPage);
      const hasNextPage = overflow.length > 0 || hasMoreRaw;
      const snapshot = {
        products,
        page,
        hasNextPage,
        hasPreviousPage: page > 1,
      };

      this.floorPages.set(page, snapshot);
      if (hasNextPage) {
        this.floorStates.set(page + 1, { cursor, overflow, hasMoreRaw });
      }
      return snapshot;
    }

    isExcludedProduct(product, { excludeSets = false } = {}) {
      const title = normalizeValue(product?.title);
      const handle = normalizeValue(product?.handle);
      const productType = normalizeValue(product?.productType);
      const hasExcludedSku = (product?.variants || []).some((variant) => (
        EXCLUDED_PRODUCT_SKUS.has(String(variant?.sku || '').trim().toUpperCase())
      ));

      return title.includes('custom')
        || handle.includes('custom')
        || EXCLUDED_PRODUCT_HANDLES.has(handle)
        || EXCLUDED_PRODUCT_TITLES.has(title)
        || hasExcludedSku
        || (excludeSets && EXCLUDED_SEARCH_PRODUCT_TYPES.has(productType));
    }

    resetSearch(query = '') {
      this.searchQuery = query.trim();
      this.searchGeneration += 1;
      this.searchPages.clear();
      this.searchCursors = new Map([[1, null]]);
    }

    async search(query, page = 1) {
      this.assertReady();
      const normalizedQuery = query.trim();
      if (!normalizedQuery) {
        return { products: [], page: 1, hasNextPage: false, hasPreviousPage: false, query: '' };
      }
      if (normalizedQuery !== this.searchQuery) this.resetSearch(normalizedQuery);
      if (this.searchPages.has(page)) return this.searchPages.get(page);
      if (!this.searchCursors.has(page)) throw new Error(`Search page ${page} is not available.`);

      const generation = this.searchGeneration;
      const broadSearch = this.api.searchProducts(normalizedQuery, {
        limit: this.itemsPerPage,
        cursor: this.searchCursors.get(page),
      });
      const exactSearch = page === 1
        ? this.api.searchProducts(`title:"${normalizedQuery.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`, {
            limit: this.itemsPerPage,
            cursor: null,
          })
        : Promise.resolve({ products: [] });
      const [result, exactResult] = await Promise.all([broadSearch, exactSearch]);
      const normalizedTitle = normalizeValue(normalizedQuery);
      const exactProducts = exactResult.products.filter((product) => (
        normalizeValue(product.title) === normalizedTitle
      ));
      const eligibleProducts = [...exactProducts, ...result.products].filter((product) => (
        !this.isExcludedProduct(product, { excludeSets: true })
      ));
      const products = eligibleProducts.filter((product, index, allProducts) => (
        allProducts.findIndex((candidate) => candidate.id === product.id) === index
      ));
      const snapshot = {
        products,
        page,
        query: normalizedQuery,
        hasNextPage: Boolean(result.hasNextPage),
        hasPreviousPage: page > 1,
      };

      if (generation !== this.searchGeneration || normalizedQuery !== this.searchQuery) {
        return { ...snapshot, stale: true };
      }

      this.searchPages.set(page, snapshot);
      if (snapshot.hasNextPage && result.nextCursor) {
        this.searchCursors.set(page + 1, result.nextCursor);
      }
      return snapshot;
    }

    destroy() {
      this.destroyed = true;
      this.wallPages.clear();
      this.wallStates.clear();
      this.floorPages.clear();
      this.floorStates.clear();
      this.searchPages.clear();
      this.searchCursors.clear();
      this.productGalleries.clear();
      this.productGalleryRequests.clear();
      this.searchGeneration += 1;
      this.api = null;
    }
  }

  namespace.CatalogService = CatalogService;
})(window);
